import React from 'react';
import { useNavigate } from 'react-router-dom';
import routes from 'router/routeList';
import NavIcon from './NavIcon';
import EmptyState from './EmptyState';
import SubscriptionOverlay from './SubscriptionOverlay';
import { CHART_COLORS } from '../constants';
import { formatCurrency, formatDate, getMonthDisplay } from '../helpers';
import styles from '../summary.module.scss';

const InicioSection = ({
  salesData,
  ordersCount,
  quotesCount,
  pendingOrders,
  pendingQuotes,
  sortedClients,
  isPremium,
  onNavChange,
}) => {
  const navigate = useNavigate();

  return (
    <div className={styles.inicioGrid}>
      {/* KPIs */}
      <div className={styles.kpiRow}>
        <div className={styles.kpiCard}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiIconWrap}>
              <NavIcon type="dollar" size={18} />
            </span>
            <span className={styles.kpiTrend}>
              <NavIcon type="trending" />
            </span>
          </div>
          <span className={styles.kpiAmount}>
            {salesData ? formatCurrency(salesData.total) : '$0.00'}
          </span>
          <span className={styles.kpiLabel}>Ventas del Mes</span>
        </div>

        <div className={styles.kpiCard}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiIconWrap}>
              <NavIcon type="file" size={18} />
            </span>
          </div>
          <span className={styles.kpiAmount}>{ordersCount}</span>
          <span className={styles.kpiLabel}>Pedidos Solicitados</span>
        </div>

        <div className={styles.kpiCard}>
          <div className={styles.kpiTop}>
            <span className={styles.kpiIconWrap}>
              <NavIcon type="chat" size={18} />
            </span>
          </div>
          <span className={styles.kpiAmount}>{quotesCount}</span>
          <span className={styles.kpiLabel}>Cotizaciones Pendientes</span>
        </div>
      </div>

      {/* Ordenes + Cotizaciones */}
      <div className={styles.twoCol}>
        <OrdersCard orders={pendingOrders} count={ordersCount} navigate={navigate} />
        <QuotesCard quotes={pendingQuotes} count={quotesCount} navigate={navigate} />
      </div>

      {/* Resumen de ventas (premium-gated) */}
      <div className={styles.reportGate}>
        <div className={`${styles.card} ${!isPremium ? styles.blurred : ''}`}>
          <div className={styles.cardHeader}>
            <div className={styles.cardHeaderLeft}>
              <h2 className={styles.cardTitle}>Resumen de Ventas</h2>
              <span className={styles.cardCount}>
                Top clientes — {getMonthDisplay(salesData?.month)}
              </span>
            </div>
            <button className={styles.cardAction} onClick={() => onNavChange('clientes')}>
              Ver reporte completo
              <NavIcon type="chevron" />
            </button>
          </div>
          <div className={styles.cardBody}>
            <div className={styles.snapshotList}>
              {sortedClients.slice(0, 5).map((client, index) => {
                const maxTotal = sortedClients[0]?.total || 1;
                const pct = (client.total / maxTotal) * 100;
                return (
                  <div key={client.name} className={styles.snapshotRow}>
                    <span
                      className={styles.snapshotRankNum}
                      style={{
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                      }}
                    >
                      {index + 1}
                    </span>
                    <span className={styles.snapshotName}>{client.name}</span>
                    <div className={styles.snapshotBarWrap}>
                      <div
                        className={styles.snapshotBar}
                        style={{
                          width: `${pct}%`,
                          backgroundColor: CHART_COLORS[index % CHART_COLORS.length],
                        }}
                      />
                    </div>
                    <span className={styles.snapshotVal}>
                      {formatCurrency(client.total)}
                    </span>
                  </div>
                );
              })}
            </div>
            {sortedClients.length > 5 && (
              <div className={styles.snapshotFooter}>
                +{sortedClients.length - 5} clientes más en el reporte completo
              </div>
            )}
          </div>
        </div>
        {!isPremium && <SubscriptionOverlay />}
      </div>
    </div>
  );
};

/* Listas de ordenes y cotizaciones pendientes */

const OrdersCard = ({ orders, count, navigate }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <div className={styles.cardHeaderLeft}>
        <h2 className={styles.cardTitle}>Órdenes por Confirmar</h2>
        <span className={styles.cardCount}>
          {count > 0 ? `${count} pendiente${count !== 1 ? 's' : ''}` : 'Sin pendientes'}
        </span>
      </div>
      <button
        className={styles.cardAction}
        onClick={() => navigate(routes.orders)}
        title="Ir a Órdenes"
      >
        {orders.length > 0 ? 'Ver todas' : 'Ir a órdenes'}
        <NavIcon type="external" size={13} />
      </button>
    </div>
    <div className={styles.cardBody}>
      {orders.length > 0 ? (
        <ul className={styles.itemList}>
          {[...orders]
            .sort((a, b) => {
              const dateA = new Date(a.deliveryDate || a.scheduleDate || a.createdAt);
              const dateB = new Date(b.deliveryDate || b.scheduleDate || b.createdAt);
              return dateB - dateA;
            })
            .slice(0, 10)
            .map((order) => (
            <li
              key={order._id || order.code}
              className={styles.item}
              onClick={() =>
                navigate(
                  `/ordenes/detalle/${order.code}/${order?.restaurant?.id}`,
                )
              }
            >
              <div className={styles.itemInfo}>
                <span className={styles.itemName}>
                  {order.restaurant?.name || 'Restaurante'}
                </span>
                <span className={styles.itemSub}>#{order.code}</span>
              </div>
              <div className={styles.itemRight}>
                <span className={styles.badgeOrder}>Solicitada</span>
                <span className={styles.itemDate}>
                  {formatDate(
                    order.deliveryDate || order.scheduleDate || order.createdAt,
                  )}
                </span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState message="Todo al día — sin órdenes pendientes" icon="file" />
      )}
    </div>
  </div>
);

const QuotesCard = ({ quotes, count, navigate }) => (
  <div className={styles.card}>
    <div className={styles.cardHeader}>
      <div className={styles.cardHeaderLeft}>
        <h2 className={styles.cardTitle}>Cotizaciones por Responder</h2>
        <span className={styles.cardCount}>
          {count > 0 ? `${count} pendiente${count !== 1 ? 's' : ''}` : 'Sin pendientes'}
        </span>
      </div>
      <button
        className={styles.cardAction}
        onClick={() => navigate(routes.cotizaciones)}
        title="Ir a Cotizaciones"
      >
        {quotes.length > 0 ? 'Ver todas' : 'Ir a cotizaciones'}
        <NavIcon type="external" size={13} />
      </button>
    </div>
    <div className={styles.cardBody}>
      {quotes.length > 0 ? (
        <ul className={styles.itemList}>
          {[...quotes]
            .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
            .slice(0, 10)
            .map((quote, index) => (
            <li
              key={quote._id || index}
              className={styles.item}
              onClick={() =>
                quote.code
                  ? navigate(`${routes.cotizaciones}/detalle_cotizacion/${quote.code}`)
                  : navigate(routes.cotizaciones)
              }
            >
              <div className={styles.itemInfo}>
                <span className={styles.itemName}>
                  {quote.restaurant?.name || quote.nameRestaurant || 'Restaurante'}
                </span>
                <span className={styles.itemSub}>
                  {quote.products?.length
                    ? `${quote.products.length} producto${
                        quote.products.length !== 1 ? 's' : ''
                      }`
                    : ''}
                </span>
              </div>
              <div className={styles.itemRight}>
                <span className={styles.badgeQuote}>Solicitada</span>
                <span className={styles.itemDate}>{formatDate(quote.createdAt)}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState message="Todo al día — sin cotizaciones pendientes" icon="chat" />
      )}
    </div>
  </div>
);

export default InicioSection;
